<template>
  <div class="w-10/12">
    <div class="w-full" v-if="!showError">
      <div style="height: 100%" v-if="loading">
        <loader size="xxs" :loader-image="false" />
      </div>
      <div v-else class="mt-6">
        <div class="w-full">
          <p class="font-semibold mb-5 inline-block">Education & Certification</p>
          <span class="float-right cursor-pointer text-flame text-base font-semibold">
            <span @click="handleClick" v-if="!edit">
              <icon icon-name="icon-edit" size="xs" class="inline-block" />
              <span class="pl-2">Edit</span>
            </span>

          </span>
        </div>

        <Certificate
          certificate-title="Educational Credentials"
          :is-edit="edit"
          :certificate-info="educationalInfo"
          certificate-type="educational"
          @add-info="addCredentials"
          @delete-educational-info="deleteEducationalInfo"
          @update-file-path="updateEducationalFilePath"
        />

        <Certificate
          certificate-title="Professional Certifications"
          :is-edit="edit"
          :certificate-info="certificationInfo"
          certificate-type="certification"
          @add-info="addCertifications"
          @delete-educational-info="deleteCertificationInfo"
          @update-file-path="updateCertificationFilePath"
        />

        <Certificate
          certificate-title="Professional Membership"
          :is-edit="edit"
          :certificate-info="membershipInfo"
          certificate-type="membership"
          @add-info="addMembership"
          @delete-educational-info="deleteMembershipInfo"
          @update-file-path="updateMembershipFilePath"
        />

        <Certificate
          certificate-title="Other Trainings"
          :is-edit="edit"
          :certificate-info="otherTrainingInfo"
          certificate-type="otherTraining"
          @add-info="addOtherTraining"
          @delete-educational-info="deleteOtherTrainingInfo"
          @update-file-path="updateOtherTrainingFilePath"
        />

        <div class="flex flex-col gap-2 mt-7 border p-4 rounded-lg">
          <div class="flex mt-2">
            <span class="text-sm font-semibold flex">Curriculum Vitae (Resume)</span>
          </div>
          <divider />

          <div class="my-5 flex items-center gap-5">
            <div v-if="!cvInfo.filePath" class="col-span-4 text-dynamicBackBtn flex items-end gap-1 text-sm font-semibold cursor-pointer" @click="triggerCVUpload">
              <div class="w-5 h-5">
                <icon icon-name="red-upload-icon" size="xs" class="icon"/>
              </div>
              Upload CV
            </div>
            <div v-else class="col-span-4 flex items-end gap-1 text-sm font-semibold cursor-pointer" @click="downloadFile(cvInfo.filePath)">
              <div class="w-5 h-5">
                <icon v-if="cvInfo.filePath" icon-name="pdf" size="xs" class="icon"/>
                <icon v-else icon-name="red-upload-icon" size="xs" class="icon"/>
              </div>
              <span class="text-flame">{{ getFileName(cvInfo.filePath) }}</span>
            </div>
            <div class="col-span-1 flex items-center justify-end" v-if="edit && cvInfo.filePath">
              <span class="flex flex-col cursor-pointer justify-center my-4"
                @click="deleteCVInfo">
                <Icon icon-name="icon-trash" size="xs" class-name="text-desire" />
              </span>
            </div>
          </div>
        </div>


      <div v-if="edit" class="line"></div>

      <div v-if="edit" class="w-full h-16 flex justify-start items-end gap-1 mt-4">
        <Button
        @click="!disable ? handleSave() : null"
          class="text-white bg-dynamicBackBtn w-36 h-11 flex justify-center items-center"
        >
          Save
        </Button>
        <Button
        @click="cancelChanges()"
        class="text-darkPurple bg-white w-20 h-11 flex justify-center items-center"
        >
          Cancel
        </Button>
      </div>
    </div>

      <Modal v-if="dialogDel">
        <Card class="p-5 flex flex-col max-w-md">
          <div class>
            <p class="font-bold ml-4 my-5 text-center">
              Are you sure you want to delete this Record?
            </p>
            <div class="flex">
              <Button class="text-white" @click="deleteQualification(dataId)"
                :background="appearance.buttonColor || defaultBTNColor">
                Yes
              </Button>
              <Button background-color="none" class="text-dynamicBackBtn border border-solid border-dynamicBackBtn ml-4"
                @click="dialogDel = false">
                Cancel
              </Button>
            </div>
          </div>
        </Card>
      </Modal>
    </div>
    <error-page v-if="showError" />
  </div>
</template>

<script>
import { mapState } from "vuex";
import {  extend } from "vee-validate";
import { required } from "vee-validate/dist/rules";
import Divider from "@/components/divider";
import Button from "@/components/Button";
import Modal from "@/components/Modal";
import Card from "@/components/Card";
import Certificate from "@/components/Certificate";
import ErrorPage from "@/modules/Admin/error403";
import axios from "axios";

extend("required", required);

export default {
  name: "EmployeeCertificate",
  components: {
    Divider,
    Modal,
    Card,
    Button,
    Certificate,
    ErrorPage
  },
  computed: {
    ...mapState({
      defaultBTNColor: (state) => state.orgAppearance.defaultBtnColor,
      appearance: (state) => state.orgAppearance.orgAppearanceData,
    }),

    shouldDisable() {
      // Check if any certificate section has valid data
      const hasEducationalData = !this.checkIsEmpty(this.educationalInfo) && !this.educationalInfo.every(item =>
        !item.institution.trim() && !item.qualification.trim() && !item.yearOfAward.trim()
      );

      const hasCertificationData = !this.checkIsEmpty(this.certificationInfo) && !this.certificationInfo.every(item =>
        !item.institution.trim() && !item.qualification.trim() && !item.yearOfAward.trim()
      );

      const hasMembershipData = !this.checkIsEmpty(this.membershipInfo) && !this.membershipInfo.every(item =>
        !item.institution.trim() && !item.qualification.trim() && (item.dateOfMembership ? !item.dateOfMembership.trim() : false)
      );

      const hasTrainingData = !this.checkIsEmpty(this.otherTrainingInfo) && !this.otherTrainingInfo.every(item =>
        !item.institution.trim() && !item.qualification.trim() && !item.yearOfAward.trim()
      );

      // Enable save if at least one certificate section has valid data
      const hasAnyValidData = hasEducationalData || hasCertificationData || hasMembershipData || hasTrainingData;

      return !hasAnyValidData; // Disable if no valid data exists
    },
  },

  data() {
    return {
      isUploaded: false,
      loading: true,
      dialogDel: false,
      edit: false,
      disable: true,
      educationalInfo: [],
      certificationInfo: [],
      membershipInfo: [],
      otherTrainingInfo: [],
      cvInfo: { filePath: null },
      dataId: "",
      showError: false,
      // Upload tracking
      currentUploadType: null, // 'otherTraining', 'cv'
      currentUploadIndex: null,
      // Original data for cancel functionality
      originalOtherTrainingInfo: [],
    };
  },

  watch: {
    shouldDisable(newValue) {
      this.disable = newValue;
    },
  },

  methods: {
    downloadFile(fileUrl) {
      if (!fileUrl) return;

      try {
        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = this.getFileName(fileUrl);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        this.$toasted.error('Failed to download file', { duration: 3000 });
      }
    },
    async handleClick() {
      if (this.$route.query.slug) {
        if (!this.edit) {
          // Store original state when entering edit mode
          this.originalOtherTrainingInfo = JSON.parse(JSON.stringify(this.otherTrainingInfo));
        }
        this.edit = !this.edit;
        return;
      }
      try {
        await this.$handlePrivilege(
          "employeeDirectory",
          "editEmployeeEducationCertification"
        );
        if (!this.edit) {
          // Store original state when entering edit mode
          this.originalOtherTrainingInfo = JSON.parse(JSON.stringify(this.otherTrainingInfo));
        }
        this.edit = !this.edit;
      } catch (error) {
        this.$toasted.error("You do not have permission to perform this task", {
          duration: 5000,
        });
      }
    },

    deleteEducationalInfo(position, data) {
      if (!data.id) {
        this.educationalInfo.splice(position, 1);
      } else {
        this.dataId = data.id;
        this.dialogDel = true;
      }
    },

    deleteCertificationInfo(position, data) {
      if (Object.values(data).every((value) => !value)) {
        this.certificationInfo.splice(position, 1);
      } else {
        this.dataId = data.id;
        this.dialogDel = true;
      }
    },

      handleSave() {
      const educationalInfoCheck = this.checkIsEmpty(this.educationalInfo);
      const certificationInfoCheck = this.checkIsEmpty(this.certificationInfo);
      const membershipInfoCheck = this.checkIsEmpty(this.membershipInfo);
      const otherTrainingInfoCheck = this.checkIsEmpty(this.otherTrainingInfo);

      const payload = {
        educationalInfo: !educationalInfoCheck
          ? this.educationalInfo.map(item => this.cleanCertificateData(item))
          : [],
        certificationInfo: !certificationInfoCheck
          ? this.certificationInfo.map(item => this.cleanCertificateData(item))
          : [],
        membershipInfo: !membershipInfoCheck
          ? this.membershipInfo.map(item => this.cleanCertificateData(item))
          : [],
        otherTrainingInfo: !otherTrainingInfoCheck
          ? this.otherTrainingInfo.map(item => this.cleanCertificateData(item))
          : [],
        cvInfo: this.cleanCVData(this.cvInfo),
      };

      this.$_createEmployeeCertifications(payload, this.$route.params.id)
        .then((response) => {
          this.$toasted.success(response.data.message, { duration: 3000 });
          this.edit = false;
        })
        .catch((error) => {
          this.$toasted.error(error.message, { duration: 3000 });
        });
    },

    checkIsEmpty(arrData) {
      return arrData.every((obj) =>
        Object.keys(obj).every((key) => obj[key] === "" || obj[key] === null || obj[key] === undefined)
      );
    },

    checkIsFull(arrData) {
      // Check that required fields (institution, qualification, yearOfAward) are filled
      // filePath and dateOfExpiration are optional
      const requiredFields = ['institution', 'qualification', 'yearOfAward'];
      return arrData.every((obj) =>
        requiredFields.every((key) => obj[key] && obj[key] !== "")
      );
    },
    addCertifications() {
      this.certificationInfo.push({
        institution: "",
        qualification: "",
        yearOfAward: "",
        filePath: null,
      });
    },

    addCredentials() {
      this.educationalInfo.push({
        institution: "",
        qualification: "",
        yearOfAward: "",
        filePath: null,
      });
    },

    deleteQualification(id) {
      this.$_deleteEducationQualification(id, this.$route.params.id)
        .then((response) => {
          this.$toasted.success(response.data.message, { duration: 3000 });
          this.loading = true;
          this.dialogDel = false;
          this.getQualifications();
        })
        .catch((error) => {
          this.$toasted.error(error.message, { duration: 3000 });
        });
    },

    getQualifications() {
      this.loading = true;
      this.educationalInfo = [];
      this.certificationInfo = [];
      this.membershipInfo = [];
      this.otherTrainingInfo = [];

      this.$_employeeCertifications(this.$route.params.id).then((response) => {
        // Educational Info
        if (response.data.educationalInfo && response.data.educationalInfo.length > 0) {
          this.educationalInfo = response.data.educationalInfo;
        } else {
          this.educationalInfo = [{
            qualification: "",
            courseOfStudy: "",
            institution: "",
            yearOfAward: null,
            filePath: null,
          }];
        }

        // Certification Info
        if (response.data.certificationInfo && response.data.certificationInfo.length > 0) {
          this.certificationInfo = response.data.certificationInfo;
        } else {
          this.certificationInfo = [{
            qualification: "",
            institution: "",
            yearOfAward: null,
            dateOfExpiration: null,
            filePath: null,
          }];
        }

        // Membership Info
        if (response.data.membershipInfo && response.data.membershipInfo.length > 0) {
          this.membershipInfo = response.data.membershipInfo;
        } else {
          this.membershipInfo = [{
            qualification: "",
            institution: "",
            dateOfMembership: null,
            dateOfExpiration: null,
            filePath: null,
          }];
        }

        // Other Training Info
        if (response.data.otherTrainingInfo && response.data.otherTrainingInfo.length > 0) {
          this.otherTrainingInfo = response.data.otherTrainingInfo;
        } else {
          this.otherTrainingInfo = [{
            institution: "",
            qualification: "",
            yearOfAward: null,
            filePath: null,
          }];
        }

        // CV Info
        this.cvInfo = response.data.cvInfo || { filePath: null };

        this.loading = false;
        this.edit = false;
        // Store original state for cancel functionality
        this.originalOtherTrainingInfo = JSON.parse(JSON.stringify(this.otherTrainingInfo));
      });
    },

    addMembership() {
      this.membershipInfo.push({
        qualification: "",
        institution: "",
        dateOfMembership: "",
        dateOfExpiration: null,
        filePath: null,
      });
    },

    addOtherTraining() {
      this.otherTrainingInfo.push({
        institution: "",
        qualification: "",
        yearOfAward: null,
        filePath: null,
      });
    },

    deleteMembershipInfo(position, data) {
      if (!data.id) {
        this.membershipInfo.splice(position, 1);
      } else {
        this.dataId = data.id;
        this.dialogDel = true;
      }
    },

    deleteOtherTrainingInfo(position, data) {
      if (!data.id) {
        this.otherTrainingInfo.splice(position, 1);
      } else {
        this.dataId = data.id;
        this.dialogDel = true;
      }
    },

    deleteCVInfo() {
      this.cvInfo.filePath = null;
    },

    updateEducationalFilePath(index, filePath) {
      if (this.educationalInfo[index]) {
        this.educationalInfo[index].filePath = filePath;
      }
    },

    updateCertificationFilePath(index, filePath) {
      if (this.certificationInfo[index]) {
        this.certificationInfo[index].filePath = filePath;
      }
    },

    updateMembershipFilePath(index, filePath) {
      if (this.membershipInfo[index]) {
        this.membershipInfo[index].filePath = filePath;
      }
    },

    updateOtherTrainingFilePath(index, filePath) {
      if (this.otherTrainingInfo[index]) {
        this.otherTrainingInfo[index].filePath = filePath;
      }
    },

    triggerOtherTrainingUpload(index) {
      this.currentUploadType = 'otherTraining';
      this.currentUploadIndex = index;
      // Trigger file input
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = '.pdf,.doc,.docx,.xlsx,.csv,.ppt,.png,.jpg,.jpeg';
      fileInput.onchange = (event) => this.handleFileUpload(event);
      fileInput.click();
    },

    triggerCVUpload() {
      this.currentUploadType = 'cv';
      // Trigger file input
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = '.pdf,.doc,.docx,.xlsx,.csv,.ppt,.png,.jpg,.jpeg';
      fileInput.onchange = (event) => this.handleFileUpload(event);
      fileInput.click();
    },

    async handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/csv',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'image/png',
        'image/jpeg',
        'image/jpg'
      ];

      if (!allowedTypes.includes(file.type)) {
        this.$toasted.error('Unsupported file type', { duration: 3000 });
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        this.$toasted.error('File size must be less than 5MB', { duration: 3000 });
        return;
      }

      try {
        this.$toasted.info('Uploading file...', { duration: 3000 });
        const formData = new FormData();
        formData.append('file', file);
        const url = process.env.VUE_APP_IMAGE_UPLOAD;
        const { data: responseData } = await axios.post(url, formData);

        if (this.currentUploadType === 'otherTraining' && this.currentUploadIndex !== undefined) {
          this.otherTrainingInfo[this.currentUploadIndex].filePath = responseData.fileUrl;
        } else if (this.currentUploadType === 'cv') {
          this.cvInfo.filePath = responseData.fileUrl;
        }

        this.$toasted.success('File uploaded successfully', { duration: 3000 });
      } catch (error) {
        this.$toasted.error('File upload failed', { duration: 3000 });
      }
    },

    getFileName(url) {
      if (!url) return 'Upload';
      const parts = url.split('/');
      const fileName = parts[parts.length - 1] || 'uploaded file';

      // Truncate filename if it's too long (max 30 characters)
      if (fileName.length > 30) {
        const extension = fileName.split('.').pop();
        const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));
        const truncatedName = nameWithoutExt.substring(0, 30);
        return `${truncatedName}...${extension}`;
      }

      return fileName;
    },
    cleanCertificateData(item) {
      // Only include necessary fields for certificate data
      const cleanItem = {};

      // Always include these fields if they exist
      if (item.qualification) cleanItem.qualification = item.qualification;
      if (item.institution) cleanItem.institution = item.institution;
      if (item.filePath) cleanItem.filePath = item.filePath;

      // Include type-specific fields
      if (item.courseOfStudy) cleanItem.courseOfStudy = item.courseOfStudy;
      if (item.yearOfAward) cleanItem.yearOfAward = item.yearOfAward;
      if (item.dateOfExpiration) cleanItem.dateOfExpiration = item.dateOfExpiration;
      if (item.dateOfMembership) cleanItem.dateOfMembership = item.dateOfMembership;

      return cleanItem;
    },
    cleanCVData(cvInfo) {
      // Only include necessary fields for CV data
      const cleanCV = {};

      if (cvInfo.filePath) cleanCV.filePath = cvInfo.filePath;

      return cleanCV;
    },

    cancelChanges() {
      // Restore original state for Other Training Info
      this.otherTrainingInfo = JSON.parse(JSON.stringify(this.originalOtherTrainingInfo));
      this.edit = false;
    },
  },

  async mounted() {
    try {
      if (!this.$route.query.slug) {
        await this.$handlePrivilege(
          "employeeDirectory",
          "viewEducationCertification"
        );
      }
      this.getQualifications();
    } catch (error) {
      this.showError = true;
    }
  }
};
</script>
<style scoped>
  .icon{
    width: 100% !important;
    height: 100% !important;
  }
</style>